
import React, { useState, useEffect } from 'react';
import OptInForm from '@/components/OptInForm';
import BenefitsList from '@/components/BenefitsList';
import EventDetails from '@/components/EventDetails';
import { useIsMobile } from '@/hooks/use-mobile';
import SeatsCounter from '@/components/SeatsCounter';
import { CalendarDays, Lock, Sparkles, Bot, Users } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';

const Index = () => {
  const isMobile = useIsMobile();
  const [showStickyBar, setShowStickyBar] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      // Show sticky bar after scrolling past 300px
      setShowStickyBar(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-100 via-purple-50 to-yellow-50">
      {/* Sticky Top Bar - Updated with new date and time */}
      <div className={`fixed top-0 left-0 right-0 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white py-2 z-50 shadow-md transition-transform duration-300 ${showStickyBar ? 'translate-y-0' : '-translate-y-full'}`}>
        <div className="container mx-auto px-4 flex flex-wrap items-center justify-center md:justify-between text-sm">
          <div className="flex items-center gap-4 flex-wrap justify-center">
            <p className="flex items-center"><Lock className="h-4 w-4 mr-1" /> Only 100 Seats Left</p>
            <p className="hidden md:block">|</p>
            <p className="flex items-center"><CalendarDays className="h-4 w-4 mr-1" /> Live May 29 @ 3:00 PM BST</p>
            <p className="hidden md:flex items-center"><Users className="h-4 w-4 ml-2 mr-1" /> Exclusively for busy parents</p>
          </div>
          <Sheet>
            <SheetTrigger asChild>
              <button className="bg-yellow-400 hover:bg-yellow-500 text-blue-900 font-bold py-1 px-4 rounded-full mt-2 md:mt-0 animate-pulse">
                Register Now
              </button>
            </SheetTrigger>
            <SheetContent>
              <div className="pt-6">
                <OptInForm />
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        {/* Hero Section */}
        <div className="mb-16">
          <div className="text-center mb-8">
            <span className="highlight-badge mb-4 bg-gradient-to-r from-pink-400 to-purple-500 text-white inline-block px-4 py-1 rounded-full text-sm font-bold flex items-center justify-center gap-2 mx-auto w-auto">
              <Sparkles className="h-4 w-4" /> FREE 60-MINUTE WEBINAR FOR PARENTS IN THE UK <Sparkles className="h-4 w-4" />
            </span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 mb-4 leading-tight">
              Your Child Has 2 Futures.<br />
              One Where They Master AI.<br />
              <span className="text-blue-600">One Where They're Controlled By It.</span><br />
              This 60-Minute Event Decides Which One.
            </h1>
            <p className="text-xl md:text-2xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
              AI isn't optional anymore — it's the new literacy.
              This 60-minute masterclass shows parents exactly how to prepare your child — before it's too late.
            </p>
            <p className="text-xl md:text-2xl text-gray-700 max-w-3xl mx-auto leading-relaxed mt-4 font-bold">
              If your child doesn't learn this now, they'll be learning it too late.
            </p>
          </div>
          
          {/* Add new high-impact callout */}
          <div className="mb-8 bg-gradient-to-r from-indigo-700 to-purple-800 text-white p-6 rounded-xl shadow-lg">
            <div className="max-w-3xl mx-auto text-center">
              <p className="text-xl md:text-2xl lg:text-3xl font-bold leading-relaxed">
                Children who learn AI now will lead.<br/>
                <span className="text-yellow-300">Those who don't… will be led.</span>
              </p>
            </div>
          </div>
          
          {/* Show form and image in different orders depending on screen size */}
          <div className="grid md:grid-cols-2 gap-8 items-start">
            {isMobile ? (
              <>
                {/* For mobile: Image first, then form */}
                <div className="mb-6">
                  <img 
                    src="/lovable-uploads/9d270462-08fb-4c19-b333-378b6fd91e0f.png" 
                    alt="Family learning AI together with children looking at a laptop" 
                    className="rounded-xl shadow-lg object-cover w-full h-auto border-4 border-purple-200"
                  />
                  <p className="text-center text-sm text-gray-500 mt-2">
                    "This is what learning looks like when your child leads, not follows."
                  </p>
                </div>
                <div>
                  <OptInForm />
                  {/* Removed duplicate seats counter message - we already have it inside the OptInForm */}
                </div>
              </>
            ) : (
              <>
                {/* For desktop: Form on left, image on right */}
                <div>
                  <OptInForm />
                  {/* Removed duplicate seats counter message - we already have it inside the OptInForm */}
                </div>
                <div className="relative">
                  <div className="absolute -top-4 -left-4 bg-yellow-300 w-12 h-12 rounded-full flex items-center justify-center transform rotate-12 z-10">
                    <Bot className="w-6 h-6 text-purple-700" />
                  </div>
                  <img 
                    src="/lovable-uploads/9d270462-08fb-4c19-b333-378b6fd91e0f.png" 
                    alt="Family learning AI together with children looking at a laptop" 
                    className="rounded-xl shadow-xl object-cover w-full h-auto border-4 border-purple-200"
                  />
                  <div className="absolute -bottom-4 -right-4 bg-pink-300 w-12 h-12 rounded-full flex items-center justify-center transform -rotate-12 z-10">
                    <Sparkles className="w-6 h-6 text-purple-700" />
                  </div>
                  <p className="text-center text-sm text-gray-700 mt-6 font-medium">
                    "This is what learning looks like when your child leads, not follows."
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
        
        {/* Consolidated Motivation Block - Merging the 3 blocks into 1 powerful statement */}
        <div className="mb-16 bg-gradient-to-r from-purple-700 to-indigo-700 text-white p-6 md:p-10 rounded-xl shadow-lg">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-xl md:text-2xl lg:text-3xl font-bold leading-relaxed">
              "Schools will teach them to memorize and repeat.<br/>
              <span className="text-yellow-300">The greatest thing you can teach them is to think critically — using AI.</span>"
            </p>
            <p className="mt-4 text-purple-100 text-lg font-medium">
              This is the edge schools can't (and won't) give them.
            </p>
          </div>
        </div>
        
        {/* Who This Is For Section */}
        <div className="mb-16 bg-white p-6 rounded-xl shadow-md">
          <h3 className="text-2xl font-bold text-center mb-6 text-purple-800">Who This Is For:</h3>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-purple-50 p-4 rounded-lg border-l-4 border-purple-500">
              <p className="font-medium text-purple-800">Busy parents who want their child to lead — not just follow</p>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
              <p className="font-medium text-blue-800">Families who value thinking, not just memorizing</p>
            </div>
            <div className="bg-indigo-50 p-4 rounded-lg border-l-4 border-indigo-500">
              <p className="font-medium text-indigo-800">Anyone who wants to give their child the most important skill of the future</p>
            </div>
          </div>
        </div>
        
        {/* What You'll Learn Section */}
        <div className="mb-16">
          <BenefitsList />
        </div>
        
        {/* Event Details Section */}
        <div className="max-w-2xl mx-auto">
          <EventDetails />
        </div>
      </div>
    </div>
  );
};

export default Index;
